import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { ecb } from '@noble/ciphers/aes.js';

import BASE_CONFIG from '../../config/config.base';
import { apiConfig } from '../../config/config.bundle';
import type { ApiResponse, UserSessionData } from '../../types/api-types';

type AuthState = {
  isInitialized?: boolean;
  user: UserSessionData | null;
  isCrossLoginModalShown?: boolean;
};

const USER_DETAILS_COOKIE_KEY = 'userDetails';
const USER_DETAILS_STORAGE_KEY = BASE_CONFIG.storageKeys.local.userDetails;
const CROSS_LOGIN_MODAL_SHOWN_STORAGE_KEY =
  'FOR_BUSINESS_CROSS_LOGIN_MODAL_SHOWN';

const getCrossApplicationUserEndpoint = () =>
  `${apiConfig.customerNetSpringBoot.baseUrl}/oAuth/crossApplicationUser`;

const getCookieDomain = () =>
  location.hostname === 'localhost' ? 'localhost' : '.geappliances.com';

const getQueryParamUuid = (): string | null => {
  if (typeof window === 'undefined' || typeof location === 'undefined') {
    return null;
  }

  const params = new URLSearchParams(location.search);
  const uuid = params.get('uuid') || sessionStorage.getItem('uuid');
  return uuid?.trim() || null;
};

const parseCrossApplicationTokenResponse = (
  responseObject: unknown
): { token?: string; user?: UserSessionData } => {
  if (!responseObject) {
    return {};
  }

  if (typeof responseObject === 'string') {
    return { token: responseObject };
  }

  if (typeof responseObject === 'object' && responseObject !== null) {
    const record = responseObject as Record<string, unknown>;

    if (
      typeof record.jwt === 'string' &&
      typeof record.sso === 'string' &&
      typeof record.firstName === 'string'
    ) {
      return { user: record as UserSessionData };
    }

    if (typeof record.token === 'string') {
      return { token: record.token };
    }

    if (typeof record.userDetailsToken === 'string') {
      return { token: record.userDetailsToken };
    }
  }

  return {};
};

const getUserFromEncryptedToken = (token: string): UserSessionData | null => {
  try {
    const key = Uint8Array.from(
      atob(BASE_CONFIG.security.userDetailsDecryptionKey),
      c => c.charCodeAt(0)
    );

    const cipherText = Uint8Array.from(atob(token), c => c.charCodeAt(0));
    const decryptedBytes = ecb(key).decrypt(cipherText);
    const rawUserDetails = new TextDecoder().decode(decryptedBytes);
    return JSON.parse(rawUserDetails) as UserSessionData;
  } catch {
    return null;
  }
};

const setUserDetailsCookie = (token: string) => {
  document.cookie = `${USER_DETAILS_COOKIE_KEY}=${token}; Path=/; Domain=${getCookieDomain()};`;
};

const deleteCrossApplicationUserRecord = async (
  uuid: string
): Promise<void> => {
  try {
    await axios.delete<ApiResponse<unknown>>(
      getCrossApplicationUserEndpoint(),
      {
        params: { uuid },
        headers: { Authorization: 'unauthorized' },
      }
    );
  } catch (error) {
    console.error('Failed to delete cross-application user record:', error);
  }
};

export const getCrossApplicationUserFallback =
  async (): Promise<UserSessionData | null> => {
    if (
      typeof window === 'undefined' ||
      typeof document === 'undefined' ||
      typeof location === 'undefined'
    ) {
      return null;
    }

    const uuid = getQueryParamUuid();
    if (!uuid) {
      return null;
    }

    const cookieMatches = document.cookie.match(
      new RegExp(`(^| )${USER_DETAILS_COOKIE_KEY}=([^;]+)`)
    );
    if (cookieMatches && cookieMatches[2]) {
      return null;
    }

    try {
      const response = await axios.get<ApiResponse<unknown>>(
        getCrossApplicationUserEndpoint(),
        {
          params: { uuid },
          headers: { Authorization: 'unauthorized' },
        }
      );

      if (!response?.data?.responseObject) {
        return null;
      }

      const { token, user } = parseCrossApplicationTokenResponse(
        response.data.responseObject
      );

      let fallbackUser: UserSessionData | null = user ?? null;
      if (!fallbackUser && token) {
        fallbackUser = getUserFromEncryptedToken(token);
      }

      if (!fallbackUser) {
        return null;
      }

      if (token) {
        setUserDetailsCookie(token);
      }

      localStorage.setItem(
        USER_DETAILS_STORAGE_KEY,
        JSON.stringify(fallbackUser)
      );
      await deleteCrossApplicationUserRecord(uuid);

      return fallbackUser;
    } catch (error) {
      console.error('Cross-application user fallback request failed:', error);
      return null;
    }
  };

const getInitialAuthState = (): AuthState => {
  let user: UserSessionData | null = null;

  const isBrowser =
    typeof window !== 'undefined' && typeof document !== 'undefined';
  if (!isBrowser) {
    return { user };
  }

  const uuid = getQueryParamUuid();
  if (uuid) {
    sessionStorage.setItem('uuid', uuid);
  }

  const cookieMatches = document.cookie.match(
    new RegExp(`(^| )${USER_DETAILS_COOKIE_KEY}=([^;]+)`)
  );

  if (cookieMatches && cookieMatches[2]) {
    try {
      const key = Uint8Array.from(
        atob(BASE_CONFIG.security.userDetailsDecryptionKey),
        c => c.charCodeAt(0)
      );

      const cipherText = Uint8Array.from(atob(cookieMatches[2]), c =>
        c.charCodeAt(0)
      );

      const decryptedBytes = ecb(key).decrypt(cipherText);
      const rawUserDetails = new TextDecoder().decode(decryptedBytes);

      localStorage.setItem(USER_DETAILS_STORAGE_KEY, rawUserDetails);
      user = JSON.parse(rawUserDetails) as UserSessionData;
      document.cookie = `${USER_DETAILS_COOKIE_KEY}=; Max-Age=0; Path=/; Domain=${location.hostname === 'localhost' ? 'localhost' : '.geappliances.com'};`;
    } catch {
      user = null;
    }
  } else {
    let rawUserDetails: string | null = null;
    if (
      typeof localStorage !== 'undefined' &&
      (rawUserDetails = localStorage.getItem(USER_DETAILS_STORAGE_KEY))
    ) {
      try {
        user = JSON.parse(rawUserDetails) as UserSessionData;
      } catch {
        user = null;
      }
    }
  }

  const isCrossLoginModalShown =
    typeof localStorage !== 'undefined' &&
    localStorage.getItem(CROSS_LOGIN_MODAL_SHOWN_STORAGE_KEY) === 'true';

  return {
    user,
    isCrossLoginModalShown,
  };
};

const authSlice = createSlice({
  name: 'auth',
  initialState: getInitialAuthState(),
  reducers: {
    setUser: (state, action: PayloadAction<UserSessionData>) => {
      state.user = action.payload;
      state.isInitialized = true;
      localStorage.setItem(
        USER_DETAILS_STORAGE_KEY,
        JSON.stringify(action.payload)
      );
    },
    logout: state => {
      state.user = null;
      localStorage.removeItem(USER_DETAILS_STORAGE_KEY);
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem(CROSS_LOGIN_MODAL_SHOWN_STORAGE_KEY);
      }
    },
    setActivePropertyId: (state, action: PayloadAction<string>) => {
      if (state.user) {
        state.user = {
          ...state.user,
          activePropertyId: action.payload,
        };
        localStorage.setItem(
          USER_DETAILS_STORAGE_KEY,
          JSON.stringify(state.user)
        );
      }
    },
    setSelectedAccountNumber: (state, action: PayloadAction<string | null>) => {
      if (state.user) {
        state.user = {
          ...state.user,
          selectedAccountNumber: action.payload,
        };
        localStorage.setItem(
          USER_DETAILS_STORAGE_KEY,
          JSON.stringify(state.user)
        );
      }
    },
    setSelectedSalesAgreementNumber: (
      state,
      action: PayloadAction<string | null>
    ) => {
      if (state.user) {
        state.user = {
          ...state.user,
          selectedSalesAgreementNumber: action.payload,
        };
        localStorage.setItem(
          USER_DETAILS_STORAGE_KEY,
          JSON.stringify(state.user)
        );
      }
    },
    setCrossLoginModalShown: (state, action: PayloadAction<boolean>) => {
      state.isCrossLoginModalShown = action.payload;
      if (typeof localStorage !== 'undefined') {
        if (action.payload) {
          localStorage.setItem(CROSS_LOGIN_MODAL_SHOWN_STORAGE_KEY, 'true');
        } else {
          localStorage.removeItem(CROSS_LOGIN_MODAL_SHOWN_STORAGE_KEY);
        }
      }
    },
  },
});

export const {
  setUser,
  logout,
  setActivePropertyId,
  setSelectedAccountNumber,
  setSelectedSalesAgreementNumber,
  setCrossLoginModalShown,
} = authSlice.actions;

export default authSlice;
